import styled, { keyframes } from "styled-components";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Wrapper = styled.section`
  position: relative;
  width: 100%;
  min-height: calc(100vh - var(--headerHeight));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #0a0a0a 0%, #141210 35%, #1a1410 50%, #0f0f0f 100%);

  &::before {
    content: "";
    position: absolute;
    top: 15%;
    right: -5%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.12) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    filter: blur(80px);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 5%;
    left: -12%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.08) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    filter: blur(100px);
  }

  @media (max-width: 768px) {
    min-height: auto;
    padding: calc(var(--headerHeight) + 32px) 16px 56px;

    &::before {
      width: 350px;
      height: 350px;
      top: 10%;
      right: -10%;
    }

    &::after {
      width: 300px;
      height: 300px;
      bottom: 0%;
      left: -15%;
    }
  }
`;

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(10, 10, 10, 0.55) 0%,
    rgba(26, 20, 16, 0.48) 35%,
    rgba(20, 16, 12, 0.52) 50%,
    rgba(15, 15, 15, 0.55) 100%
  );
  z-index: 2;
  pointer-events: none;
  backdrop-filter: blur(0.8px);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: 
      repeating-linear-gradient(
        0deg,
        rgba(212, 175, 55, 0.02) 0px,
        transparent 2px,
        transparent 4px,
        rgba(212, 175, 55, 0.01) 6px
      );
    pointer-events: none;
    opacity: 0.3;
  }
`;

export const Content = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 48px 36px;
  background: linear-gradient(135deg, rgba(16, 14, 12, 0.45) 0%, rgba(14, 12, 10, 0.4) 100%);
  border: 1.5px solid rgba(212, 175, 55, 0.25);
  border-radius: 32px;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  box-shadow: 
    0 16px 60px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(212, 175, 55, 0.2),
    0 0 100px rgba(212, 175, 55, 0.1);
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% -15%,
      rgba(212, 175, 55, 0.15) 0%,
      rgba(212, 175, 55, 0) 65%
    );
    pointer-events: none;
    border-radius: 32px;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0) 40%, rgba(255, 255, 255, 0.08));
    opacity: 0.2;
    pointer-events: none;
    transform: translateX(-120%);
    animation: shimmer 18s linear infinite;
    border-radius: 32px;
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-120%);
    }
    100% {
      transform: translateX(120%);
    }
  }

  .heroCopy {
    display: inline-grid;
    gap: 24px;
    margin-bottom: 32px;
    align-items: center;
    justify-items: center;
  }

  h1 {
    font-size: clamp(3.2rem, 6vw, 4.8rem);
    margin: 0;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #fafafa;
    font-weight: 900;
    line-height: 0.92;
    text-shadow: 0 32px 64px rgba(212, 175, 55, 0.2);
    font-family: 'Segoe UI', Trebuchet MS, sans-serif;
    position: relative;
    padding-bottom: 16px;
    background: none;
    -webkit-background-clip: unset;
    -webkit-text-fill-color: unset;
    background-clip: unset;
  }

  h1::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #d4af37, transparent);
    border-radius: 2px;
    box-shadow: 0 0 32px rgba(212, 175, 55, 0.6);
  }

  h2 {
    font-size: clamp(1.15rem, 1.7vw, 1.65rem);
    margin: 0;
    color: #e8e4da;
    font-weight: 400;
    letter-spacing: 0.02em;
    max-width: 96%;
    line-height: 1.6;
    opacity: 0.96;
    font-family: 'Segoe UI', Trebuchet MS, sans-serif;
    font-style: italic;
  }

  .category {
    margin: 0;
    font-size: clamp(0.82rem, 1.15vw, 0.92rem);
    color: #d4af37;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    opacity: 0.78;
    line-height: 1.8;
    font-weight: 500;
    word-spacing: 0.4em;
  }

  .description {
    margin: 0;
    font-size: clamp(0.95rem, 1.25vw, 1.1rem);
    color: #d0cfc8;
    font-weight: 400;
    letter-spacing: 0.01em;
    line-height: 1.75;
    max-width: 99%;
    opacity: 0.9;
    font-style: normal;
  }

  .trustIndicators {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 16px;
    justify-content: center;
    align-items: center;
    margin-top: 16px;
    font-size: 0.82rem;
    color: #b8b8b8;
    letter-spacing: 0.08em;
    width: 100%;

    span {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 12px 16px;
      background: rgba(212, 175, 55, 0.04);
      border: 1px solid rgba(212, 175, 55, 0.12);
      border-radius: 12px;
      transition: all 0.3s ease;
      position: relative;
      color: #b8b8b8;
      font-weight: 600;

      &::before {
        content: "";
        display: inline-block;
        width: 5px;
        height: 5px;
        background: #d4af37;
        border-radius: 50%;
      }

      &:first-child::before {
        content: "★";
        width: auto;
        height: auto;
        background: none;
        color: #d4af37;
        font-size: 0.9rem;
      }

      &:hover {
        background: rgba(212, 175, 55, 0.08);
        border-color: rgba(212, 175, 55, 0.25);
        transform: translateY(-2px);
        color: #ffffff;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 48px 32px 42px;
    gap: 24px;

    .heroCopy {
      gap: 22px;
      margin-bottom: 36px;
    }

    .trustIndicators {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      font-size: 0.75rem;

      span {
        padding: 10px 12px;
      }
    }

    h1 {
      padding-bottom: 20px;

      &::after {
        width: 140px;
        height: 3px;
      }
    }
  }

  @media (max-width: 480px) {
    padding: 32px 22px 28px;

    .heroCopy {
      gap: 16px;
      margin-bottom: 24px;
    }

    .trustIndicators {
      grid-template-columns: 1fr;
      gap: 10px;
      font-size: 0.7rem;

      span {
        padding: 8px 10px;
      }
    }

    h1 {
      font-size: 2.8rem;
      letter-spacing: 0.14em;
      padding-bottom: 14px;

      &::after {
        width: 110px;
        height: 2.5px;
      }
    }

    h2 {
      font-size: 1.15rem;
      max-width: 100%;
    }

    p.description {
      font-size: 0.9rem;
    }
  }
`;

export const HeroButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 36px;
  max-width: 100%;
  gap: 8px;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 50%, #c4941a 100%);
  background-size: 200% 200%;
  color: #1a1410;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  border-radius: 32px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0;
  animation: ${fadeInUp} 1s ease-out 0.6s forwards;
  box-shadow: 0 12px 40px rgba(212, 175, 55, 0.25);
  position: relative;
  overflow: hidden;
  text-decoration: none;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 24px 60px rgba(212, 175, 55, 0.4);
    background-position: 200% center;
    letter-spacing: 1.4px;
  }

  &:hover::before {
    left: 100%;
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 16px 40px;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 14px 32px;
    font-size: 0.85rem;
    letter-spacing: 1px;
  }
`;

export const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: ${({ bgImage }) => bgImage ? `url(${bgImage})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(10, 10, 10, 0.6);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle, var(--accentGold) 0%, transparent 70%);
    opacity: 0.05;
    z-index: 1;
    pointer-events: none;
  }
`;